import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type {
  OrgEffectiveRateLimitRow,
  OrgSkuRateCore,
  QuotaLane,
  RateLimitSource,
  SkuRateLimitRecord,
} from '@nadoutong/contracts';
import {
  useOrgEffectiveRateLimits,
  useSkuRateLimits,
} from '../../api/useRoutingLoad.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Panel } from '../../ui/index.ts';
import { formatTpmCap } from '../quotas/tpmUnits.ts';
import { groupSkusByNamespace } from './groupSkusByNamespace.ts';

const SOURCE_KEY: Record<RateLimitSource, I18nKey> = {
  org_sku: 'quotas.editor.source.org_sku',
  org_all: 'quotas.editor.source.org_all',
  sku: 'quotas.editor.source.sku',
  global: 'quotas.editor.source.global',
  none: 'quotas.editor.source.none',
};

function formatCap(value: number | null | undefined, unlimited: string): string {
  return typeof value === 'number' ? String(value) : unlimited;
}

function RateLimitRow({
  sku,
  skuDefault,
  effective,
  quotaLane,
}: {
  readonly sku: OrgSkuRateCore;
  readonly skuDefault: SkuRateLimitRecord | null;
  readonly effective: OrgEffectiveRateLimitRow | null;
  readonly quotaLane: QuotaLane;
}): JSX.Element {
  const t = useT();
  const unlimited = t('customer_manager_360.rate_limits.unlimited');
  const image = quotaLane === 'image';
  const tpmLabel = image ? '—' : formatTpmCap(skuDefault?.tpm, unlimited);
  const tpmEffective = image ? '—' : formatTpmCap(effective?.tpm, unlimited);

  return (
    <div
      className="grid items-start gap-3 border-b border-line-soft py-3 last:border-b-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]"
      data-testid={`customer-360-rate-limit-row-${sku.sku_id}`}
    >
      <div className="min-w-0">
        <p className="m-0 truncate font-mono text-dense text-paper">{sku.sku_name}</p>
        <p className="mb-0 mt-0.5 font-mono text-micro text-paper-low">{sku.sku_id}</p>
      </div>
      <div className="text-dense text-paper-low" data-testid={`customer-360-rate-limit-sku-default-${sku.sku_id}`}>
        <span className="block">{t('customer_manager_360.rate_limits.rpm')}: {formatCap(skuDefault?.rpm, unlimited)}</span>
        <span className="block">{t('customer_manager_360.rate_limits.tpm')}: {tpmLabel}</span>
        {image ? (
          <span className="mt-1 block text-micro text-paper-low">{t('quotas.image_lane.rpm_only')}</span>
        ) : null}
      </div>
      <div className="text-dense text-paper" data-testid={`customer-360-rate-limit-effective-${sku.sku_id}`}>
        <span className="block">
          {t('customer_manager_360.rate_limits.rpm')}: {formatCap(effective?.rpm, unlimited)}
          {' '}({t(SOURCE_KEY[effective?.rpm_source ?? 'none'])})
        </span>
        <span className="block">
          {t('customer_manager_360.rate_limits.tpm')}: {tpmEffective}
          {image ? null : <> {' '}({t(SOURCE_KEY[effective?.tpm_source ?? 'none'])})</>}
        </span>
      </div>
    </div>
  );
}

export function RateLimitsTab({
  orgId,
  skus,
}: {
  readonly orgId: string;
  readonly skus: readonly OrgSkuRateCore[];
}): JSX.Element {
  const t = useT();
  const skuDefaults = useSkuRateLimits();
  const orgEffective = useOrgEffectiveRateLimits(orgId, orgId.length > 0);
  const groups = useMemo(
    () => groupSkusByNamespace(skus.map((sku) => ({ sku }))),
    [skus],
  );
  const skuDefaultById = useMemo(() => {
    const map = new Map<string, SkuRateLimitRecord>();
    for (const item of skuDefaults.data?.items ?? []) {
      map.set(item.sku_id, item);
    }
    return map;
  }, [skuDefaults.data]);
  const quotaLaneById = useMemo(() => {
    const map = new Map<string, QuotaLane>();
    for (const item of skuDefaults.data?.quota_lanes ?? []) {
      map.set(item.sku_id, item.quota_lane);
    }
    return map;
  }, [skuDefaults.data]);
  const effectiveById = useMemo(() => {
    const map = new Map<string, OrgEffectiveRateLimitRow>();
    for (const item of orgEffective.data?.rows ?? []) {
      map.set(item.sku_id, item);
    }
    return map;
  }, [orgEffective.data]);

  return (
    <Panel
      testId="customer-manager-360-rate-limits"
      caption={t('customer_manager_360.rate_limits.title')}
      padded
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense leading-5 text-paper-low">
          {t('customer_manager_360.rate_limits.hint')}
        </p>
        <Link
          to={`/quotas?org=${encodeURIComponent(orgId)}`}
          className="text-dense text-signal hover:text-signal-bright"
          data-testid="customer-360-rate-limit-edit-link"
        >
          {t('quotas.link_edit')}
        </Link>
        {skuDefaults.isPending || orgEffective.isPending ? (
          <p className="m-0 text-dense text-paper-low">{t('customer_manager_360.rate_limits.loading')}</p>
        ) : skus.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('customer_manager_360.rate_limits.empty')}</p>
        ) : (
          groups.map((group) => (
            <section key={group.namespaceSlug} className="flex flex-col">
              <h3 className="m-0 mb-1 font-mono text-label text-paper-mid">
                {group.vendor}/{group.namespaceSlug}
              </h3>
              {group.skus.map((row) => (
                <RateLimitRow
                  key={row.sku.sku_id}
                  sku={row.sku}
                  skuDefault={skuDefaultById.get(row.sku.sku_id) ?? null}
                  effective={effectiveById.get(row.sku.sku_id) ?? null}
                  quotaLane={
                    effectiveById.get(row.sku.sku_id)?.quota_lane
                    ?? quotaLaneById.get(row.sku.sku_id)
                    ?? 'llm'
                  }
                />
              ))}
            </section>
          ))
        )}
      </div>
    </Panel>
  );
}
